/**
 * Capacity Method & Inputs.
 *
 * This is where a department owner decides how their department is measured — the drivers,
 * the thresholds, the MAX/SUM combination mode, and whether the result is validated for
 * reporting. Those are Configuration Decisions in the UAT guide's language: the model
 * calculating correctly and the department still having to determine the right method for
 * its own workload are different things, and only the second belongs to the owner.
 */
import { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useModel, useDepartmentSignals } from '../state.jsx';
import { getAccessToken } from '../auth.jsx';
import { Icon } from '../components/IconSprite.jsx';
import { BuiltPositions } from './BuiltPositions.jsx';
import { DataTable } from '../components/DataTable.jsx';

/**
 * The Configure landing page, exactly as the legacy build groups it: a card per area,
 * under four headings. The descriptions are the originals — several of them state which
 * inputs are legacy and no longer drive the live signal, which is precisely the thing a
 * department owner needs to know before spending time on a screen.
 */
const CATALOGUE = [
  ['Your Data', [
    ['depts', 'ic-building', 'Departments', (s) => `${s.depts.length} depts`,
      'Department names, codes, colors, and per-department hiring alert thresholds.'],
    ['hcplan', 'ic-layers', 'Built — Import & Positions', (s) => `${s.headcountPlan.reduce((a, p) => a + (p.count ?? 0), 0)} seats`,
      'Import a single Built export containing both approved positions and current employees. Built is the one source of truth — positions, who fills them, vacancies, funding, budget, and the Needed-By forecast for budget season all live here.'],
    ['roles', 'ic-badge', 'Role Profiles', (s) => `${s.roles.length} roles`,
      'Define how each role splits their time across activity types. Legacy input — still feeds Team Fragility and Coverage Risk’s Absence Exposure, but department tiles, Hiring Alerts, and Business Case no longer depend on it.'],
    ['activities', 'ic-clipboard', 'Activity Types', (s) => `${s.acts.length} types`,
      'Work categories used in role profiles. Legacy input for Team Fragility and Coverage Risk’s Absence Exposure — not the department tiles, Hiring Alerts report, or Business Case.'],
    ['import', 'ic-upload', 'Built Import', null,
      'Load a position register exported from Built. Previews the match before anything is written.'],
    ['quarantine', 'ic-alert-tri', 'Import Quarantine', (s) => s.importQuarantine.length ? `${s.importQuarantine.length} held` : null,
      'Positions or employees an import couldn’t match to a real department or role. Excluded from every calculation until manually mapped here — never auto-assigned to a fallback.'],
  ]],
  ['Capacity Rules', [
    ['haircut', 'ic-scissors', 'Capacity Haircuts', null,
      'PTO, holidays, sick days, interruption buffer, and ramp periods. Ramp Productivity and the time-off assumption feed volume-threshold departments’ live productive FTE; the rest feeds Team Fragility and Coverage Risk’s Absence Exposure.'],
    ['settings', 'ic-gauge', 'Thresholds', null,
      'Turnover rate, Org Headcount, HR Benchmark Ratio, and Forward Planning Horizon are live. FTE gap / consecutive weeks / elevated / critical / target % thresholds shown on this panel are retired — no current calculation effect.'],
  ]],
  ['Housekeeping', [
    ['export', 'ic-download', 'Export & Save', null,
      'Download the configuration as JSON, or the whole model as a five-sheet Excel workbook.'],
  ]],
  ['Help & Diagnostics', [
    ['howto', 'ic-book', 'How It Works', null,
      'The rules behind every number — each signal type, both gates, and what deliberately does not affect a status.'],
    ['selfcheck', 'ic-flask', 'Run Model Self-Check', null,
      'A fixed-data regression suite against the model’s own calculation code. Run it before you start.'],
  ]],
];

function Panel({ title, lead, note, children }) {
  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-title">{title}</div>
        {lead && <div className="page-hdr-sub">{lead}</div>}
      </div>
      {children}
      {note && <p className="page-note">{note}</p>}
    </>
  );
}

function Departments() {
  const { state, patch, isAdmin } = useModel();
  const signals = useDepartmentSignals();
  const [error, setError] = useState(null);

  const byId = useMemo(
    () => Object.fromEntries(signals.map((s) => [s.dep.id, s])),
    [signals],
  );

  async function toggleValidated(dep, next) {
    setError(null);
    try {
      await patch('depts', dep.id, { approvedForReporting: next });
    } catch (err) { setError(err.message); }
  }

  const columns = [
    { key: 'name', label: 'Department', type: 'text', width: '20%' },
    { key: 'type', label: 'Type', type: 'select', options: ['service', 'support', 'judgment'], width: '10%' },
    { key: 'fteTrig', label: 'FTE threshold', type: 'number', step: '0.1', align: 'right', width: '10%' },
    {
      key: 'drivers', label: 'Volume drivers', width: '24%',
      render: (d) =>
        d.volDrivers?.length
          ? <span className="muted">{d.volDrivers.map((v) => `${v.name} — ${v.current?.toLocaleString()} @ ${v.perFTE}/FTE`).join(' · ')}</span>
          : <span className="muted">—</span>,
    },
    {
      key: 'volCombineMode', label: 'Combine', width: '12%',
      render: (d) => {
        if (!(d.volDrivers?.length > 1)) return <span className="muted">—</span>;
        return (
          <span>
            <span className="chip pill-blue">{(d.volCombineMode ?? 'max').toUpperCase()}</span>{' '}
            {!d.volCombineLocked && <span className="chip pill-amber">unlocked</span>}
          </span>
        );
      },
    },
    {
      key: 'status', label: 'Status', width: '10%',
      render: (d) => {
        const s = byId[d.id]?.signal;
        if (!s) return '—';
        const [label, cls] = s.dataInvalid ? ['Data Invalid', 'chip-purple']
          : s.notConfigured ? ['Not Configured', 'chip-grey']
          : s.flagged ? ['Flagged', 'chip-red']
          : s.watch ? ['Watch', 'chip-amber']
          : s.kind === 'judgment' ? ['Informational', 'chip-blue']
          : ['Healthy', 'chip-green'];
        return <span className={`chip ${cls}`}>{label}</span>;
      },
    },
    {
      key: 'approvedForReporting', label: 'Validated', align: 'center', width: '10%',
      render: (d) => (
        <input
          type="checkbox"
          checked={!!d.approvedForReporting}
          disabled={!isAdmin}
          onChange={(e) => toggleValidated(d, e.target.checked)}
          title="Validated for Reporting — gates the Executive Summary's totals only"
        />
      ),
    },
  ];

  return (
    <Panel
      title="Departments"
      lead="How each department is measured, and whether its result counts as a validated organizational finding yet."
      note="The Validation Gate controls three things and no others: the Executive Summary tab's totals, the draft-pilot banner, and the Hiring Alerts report's Reporting Scope column. It does NOT gate Department Status, Business Case, or any export — those always show every department's real number. An unvalidated department's Flagged status is real formula output, just not presented as official yet. Raising a department's FTE threshold above its current gap moves it from Flagged to Watch, never to Healthy: Healthy requires a gap of zero or less, and a threshold cannot make a positive gap disappear."
    >
      {error && <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--red)', border: '1px solid var(--red)' }}>{error}</div>}
      <DataTable collection="depts" columns={columns} records={state.depts} />
    </Panel>
  );
}

function Roles() {
  const { state } = useModel();
  const deptName = (id) => state.depts.find((d) => d.id === id)?.name ?? id;
  const columns = [
    { key: 'name', label: 'Role', type: 'text', width: '22%' },
    { key: 'dept', label: 'Department', render: (r) => deptName(r.dept), width: '16%' },
    { key: 'hrs', label: 'Hrs/wk', type: 'number', align: 'right', width: '8%' },
    { key: 'tth', label: 'Time to hire (wks)', type: 'number', align: 'right', width: '12%' },
    { key: 'rampWks', label: 'Ramp (wks)', type: 'number', align: 'right', width: '10%' },
    { key: 'rampProd', label: 'Ramp productivity', type: 'number', step: '0.05', align: 'right', width: '12%' },
    {
      key: 'supportMeta', label: 'Support lane', width: '12%',
      render: (r) => r.supportMeta?.driver
        ? <span className="chip pill-grey">{r.supportMeta.driver}</span>
        : <span className="muted">—</span>,
    },
    { key: 'contractorViable', label: 'Contractor viable', type: 'checkbox', align: 'center', width: '10%' },
  ];
  return (
    <Panel
      title="Roles"
      lead={`${state.roles.length} roles across the organization.`}
      note="Ramp productivity is the role's own documented figure and is applied only while someone is genuinely inside their ramp window. A junior whose ramp has finished counts as a full FTE, the same as anyone else — this is not a permanent discount by seniority."
    >
      <DataTable collection="roles" columns={columns} records={state.roles} />
    </Panel>
  );
}

function Activities() {
  const { state } = useModel();
  const columns = [
    { key: 'order', label: '#', align: 'right', width: '6%', render: (a) => <span className="num-mono">{a.order}</span> },
    { key: 'name', label: 'Activity', type: 'text', width: '26%' },
    { key: 'billable', label: 'Billable', type: 'checkbox', align: 'center', width: '10%' },
    { key: 'desc', label: 'Description', type: 'text', width: '44%' },
    {
      key: 'depts', label: 'Scope', width: '14%',
      render: (a) => a.depts?.length
        ? <span className="muted">{a.depts.length} dept(s)</span>
        : <span className="chip pill-grey">org-wide</span>,
    },
  ];
  return (
    <Panel
      title="Activities"
      lead="The activity mix each role's time is distributed across."
      note="Order matters here: every role's activity percentages are stored positionally against this list, so reordering activities would reassign every role's mix. That ordering is stored explicitly rather than left to chance."
    >
      <DataTable collection="acts" columns={columns} records={[...state.acts].sort((a, b) => a.order - b.order)} />
    </Panel>
  );
}

function Haircuts() {
  const { state, engine } = useModel();
  const h = state.haircut;
  const availability = engine.timeOffAvailabilityMult();
  const offDays = (h.pto ?? 0) + (h.hol ?? 0) + (h.sick ?? 0);

  return (
    <Panel
      title="Haircuts"
      lead="The organization-wide time-off assumption behind productive FTE."
      note="These three fields feed productive FTE directly for volume-threshold departments, not just Team Fragility. Task-switching interruption and turnover are deliberately kept separate: being at work but context-switching, and a future vacancy risk, are different things from simply being out."
    >
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
        <div className="kpi"><div className="kpi-label">Annual PTO</div><div className="kpi-value">{h.pto}</div><div className="kpi-sub">days</div></div>
        <div className="kpi"><div className="kpi-label">Holidays</div><div className="kpi-value">{h.hol}</div><div className="kpi-sub">days</div></div>
        <div className="kpi"><div className="kpi-label">Sick</div><div className="kpi-value">{h.sick}</div><div className="kpi-sub">days</div></div>
        <div className="kpi"><div className="kpi-label">Interruption</div><div className="kpi-value">{h.interrupt}%</div><div className="kpi-sub">not in productive FTE</div></div>
        <div className="kpi" style={{ gridColumn: '1 / -1' }}>
          <div className="kpi-label">Resulting availability</div>
          <div className="kpi-value">{(availability * 100).toFixed(2)}%</div>
          <div className="kpi-sub">{260 - offDays} of 260 working days — the multiplier applied to every productive-FTE calculation</div>
        </div>
      </div>
    </Panel>
  );
}

function Settings() {
  const { state } = useModel();
  const s = state.settings;
  const rows = [
    ['Organization headcount', s.orgHeadcount, 'Divides every support-lane ratio. A missing or zero value gates those departments to Data Invalid rather than falling back to a guess.'],
    ['HR benchmark ratio', `1:${s.hrBenchmarkRatio}`, 'Default support benchmark; a department can override it with its own.'],
    ['Default FTE threshold', s.fte, 'Used when a department has not set its own.'],
    ['Forward planning horizon', `${s.hz} weeks`, 'How far out Anticipated Demand is counted for the forward-looking figure.'],
    ['Annual turnover', `${s.turnover}%`, 'Legacy capacity engine only — feeds Team Fragility, not the live signal.'],
  ];
  return (
    <Panel title="Settings" lead="Organization-wide values with cross-department reach.">
      <div className="table-scroll">
        <table className="tbl">
          <thead><tr><th style={{ width: '24%' }}>Setting</th><th style={{ width: '14%' }}>Value</th><th>Effect</th></tr></thead>
          <tbody>
            {rows.map(([label, value, effect]) => (
              <tr key={label}>
                <td><strong>{label}</strong></td>
                <td className="num-mono">{value}</td>
                <td className="muted">{effect}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="page-note">
        Changing Organization Headcount or the HR Benchmark Ratio recalculates every support
        department's status at once — a single settings change with organization-wide reach.
      </p>
    </Panel>
  );
}

function Quarantine() {
  const { state } = useModel();
  const columns = [
    { key: 'posTitle', label: 'Position title', width: '22%' },
    { key: 'rawDept', label: 'Department (as imported)', width: '20%' },
    { key: 'incumbent', label: 'Incumbent', width: '18%' },
    { key: 'builtPosId', label: 'Position ID', width: '14%' },
    {
      key: 'matched', label: 'Unmatched on', width: '26%',
      render: (q) => [!q.deptMatched && 'department', !q.roleMatched && 'role'].filter(Boolean).join(', ') || '—',
    },
  ];
  return (
    <Panel
      title="Import Quarantine"
      lead="Rows from a Built import that could not be matched to a known department or role."
      note="Held here rather than dropped, guessed at, or merged into an unrelated position. Resolving a record requires a role that actually belongs to the chosen department, and groups into an existing position by Position ID — not by role, department, status and funding alone, which could combine two real positions that only coincidentally share those four fields."
    >
      <DataTable collection="importQuarantine" columns={columns} records={state.importQuarantine} empty="Nothing quarantined — every imported row matched a known department and role." />
    </Panel>
  );
}

function SelfCheck() {
  const { api } = useModel();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  async function run() {
    setRunning(true);
    try {
      setResult(await api('/self-check'));
    } catch (err) {
      setResult({ passed: false, error: err.message, results: [] });
    } finally {
      setRunning(false);
    }
  }

  const groups = useMemo(() => {
    if (!result?.results) return [];
    const map = new Map();
    for (const r of result.results) {
      const g = map.get(r.group) ?? { group: r.group, total: 0, passed: 0, failures: [] };
      g.total++;
      if (r.pass) g.passed++; else g.failures.push(r);
      map.set(r.group, g);
    }
    return [...map.values()];
  }, [result]);

  return (
    <Panel
      title="Run Model Self-Check"
      lead="A fixed-data regression suite against the model's own calculation code."
      note="It runs on built-in test fixtures, not your configuration, so it never scales with the size of your real dataset. It doesn't replace a manual walkthrough of the UAT guide, but a clean run before you start confirms the underlying calculations haven't regressed."
    >
      <button className="btn btn-primary" onClick={run} disabled={running}>
        {running ? 'Running…' : 'Run self-check'}
      </button>

      {result && (
        <div style={{ marginTop: 18 }}>
          <div className="alert" style={result.passed
            ? { background: 'var(--ltgrn)', color: 'var(--grn)', border: '1px solid var(--grn)' }
            : { background: 'var(--ltred)', color: 'var(--red)', border: '1px solid var(--red)' }}>
            {result.error
              ? `Could not run: ${result.error}`
              : result.passed
                ? `All ${result.total} assertions passed.`
                : `${result.failureCount} of ${result.total} assertions failed.`}
          </div>
          <div className="table-scroll" style={{ marginTop: 12 }}>
            <table className="tbl">
              <thead><tr><th>Group</th><th style={{ textAlign: 'right' }}>Passed</th><th>Failures</th></tr></thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.group}>
                    <td>{g.group}</td>
                    <td style={{ textAlign: 'right' }} className="num-mono">{g.passed}/{g.total}</td>
                    <td className="muted">
                      {g.failures.length === 0
                        ? <span className="chip pill-green">clean</span>
                        : g.failures.map((f) => `${f.desc}: ${f.detail}`).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function HowItWorks() {
  return (
    <Panel title="How It Works" lead="The rules behind every number, in one place.">
      <div className="prose">
        <h3>Every department resolves to exactly one status</h3>
        <p>
          Healthy, Watch, Flagged, Data Invalid, Informational, or Not Configured — through one
          of five type-specific engines. Manager Insight can push a status up a level, never down.
        </p>

        <h3>Volume threshold</h3>
        <p>
          Gap = implied FTE − productive FTE. Healthy when the gap is zero or less; Watch when it
          is positive but under the department's own threshold; Flagged at or over it. Each
          driver's requirement is <code>volume ÷ per-FTE rate</code>; multiple drivers combine
          under MAX (alternative measures of the same work) or SUM (separate, additive work).
          Productive FTE is ramp-weighted and reduced by the org-wide time-off assumption.
        </p>

        <h3>Coverage</h3>
        <p>
          Healthy with no uncovered and no single-point blocks; Watch with at least one
          single-point block; Flagged with any fully uncovered block. A configured department
          with zero real staff is always Flagged, whatever the grid says.
        </p>

        <h3>Support</h3>
        <p>
          Three independently-thresholded lanes — headcount ratio, requisition load, and a
          payroll/volume ceiling. The department flags if any lane alerts and watches if none
          alert but one warns. Blending them into one ratio would mix unlike scaling patterns.
        </p>

        <h3>Pure judgment</h3>
        <p>
          No formula, ever. Status is Informational unless a manager's own read escalates it.
          Expertise-driven work needs a human judgment call, not a fabricated number.
        </p>

        <h3>Not Configured vs. Data Invalid</h3>
        <p>
          <strong>Not Configured</strong> means no roles or roster exist — a data gap, not a
          proven shortage. <strong>Data Invalid</strong> means something actually entered is
          broken: a zero or negative per-FTE rate, a missing organization headcount, an
          impossible weekly-hours figure. Every failing input is listed by name. Neither is a
          synonym for "looks fine".
        </p>

        <h3>What deliberately does not affect a status</h3>
        <p>
          Seniority tags, departure risk, Anticipated Demand, Team Output, approved-but-unfilled
          seats, and Needed-By dates. Each is real information, and each is kept out of the live
          signal on purpose.
        </p>
      </div>
    </Panel>
  );
}

function BuiltImport() {
  const { api, isAdmin } = useModel();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  async function send(stage) {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const token = await getAccessToken();
      const res = await fetch(`/api/import/built/${stage}`, {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Import failed (${res.status})`);
      if (stage === 'preview') { setPreview(json.analysis); setDone(null); }
      else { setDone(json); setPreview(null); setFile(null); window.location.reload(); }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title="Built Import"
      lead="Load the position register exported from Built. Nothing is written until you confirm."
      note="A repeat import matches each row against the existing roster by Built's Position ID first, falling back to an unambiguous name + department + role only when no Position ID is mapped. A match updates that person in place, keeping their internal identity, so manager-entered seniority, departure risk and corrected hours survive and nothing that referenced them — an outage, a Team Output entry — is orphaned. A previously-imported person absent from the new file is flagged, never deleted. Rows that cannot be tied to a known department and role are quarantined rather than guessed at."
    >
      {!isAdmin && <p className="page-note">Importing requires the Admin role.</p>}

      <div className="kpi" style={{ gridColumn: '1 / -1' }}>
        <div className="kpi-label">Position register (.xlsx or .csv)</div>
        <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            disabled={!isAdmin || busy}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setError(null); }}
          />
          <button className="btn btn-secondary" disabled={!file || busy} onClick={() => send('preview')}>
            {busy && !preview ? 'Reading…' : 'Preview'}
          </button>
        </div>
      </div>

      {error && <div className="alert" style={{ marginTop: 14, background: 'var(--ltred)', color: 'var(--red)', border: '1px solid var(--red)' }}>{error}</div>}
      {done && <div className="alert" style={{ marginTop: 14 ,  background: 'var(--ltgrn)', color: 'var(--grn)', border: '1px solid var(--grn)' }}>Imported {done.matched} rows from {done.fileName}.</div>}

      {preview && (
        <>
          <h2 className="exec-section" style={{ marginTop: 22, marginBottom: 8 }}>What this import would do</h2>
          <div className="kpi-grid kpi-4">
            <div className="kpi">
              <div className="kpi-label">Rows matched</div>
              <div className="kpi-value">{preview.matched}</div>
              <div className="kpi-sub">of {preview.rowsRead} read</div>
            </div>
            <div className={`kpi ${preview.quarantined ? 'amber' : 'green'}`}>
              <div className="kpi-label">Quarantined</div>
              <div className="kpi-value">{preview.quarantined}</div>
              <div className="kpi-sub">held for manual mapping</div>
            </div>
            <div className="kpi blue">
              <div className="kpi-label">Roster after merge</div>
              <div className="kpi-value">{preview.rosterAfterMerge}</div>
              <div className="kpi-sub">from {preview.rosterBefore}, before vacancies are re-linked</div>
            </div>
            <div className={`kpi ${preview.markedStale ? 'amber' : 'green'}`}>
              <div className="kpi-label">Marked stale</div>
              <div className="kpi-value">{preview.markedStale}</div>
              <div className="kpi-sub">flagged, never deleted</div>
            </div>
          </div>

          {preview.duplicatePositionIds?.length > 0 && (
            <div className="alert" style={{ marginTop: 12 ,  background: 'var(--ltamber)', color: '#92400E', border: '1px solid var(--amber)' }}>
              This file contains the same Position ID more than once
              ({preview.duplicatePositionIds.join(', ')}). That is a duplicate in the source
              file, not a re-import — check it before committing.
            </div>
          )}

          <h3 style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink3)', margin: '18px 0 8px' }}>
            Detected column mapping
          </h3>
          <div className="table-scroll">
            <table className="tbl">
              <thead><tr><th>Field</th><th>Column</th></tr></thead>
              <tbody>
                {Object.entries(preview.mapping).map(([field, index]) => (
                  <tr key={field}>
                    <td><strong>{field}</strong></td>
                    <td className="muted">{preview.headers[index]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => send('commit')}>
              {busy ? 'Importing…' : `Commit import of ${preview.matched} rows`}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

function ExportPanel() {
  const { state, isAdmin } = useModel();
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  /**
   * Downloads go through fetch rather than a plain link so the bearer token is attached —
   * an <a href> cannot carry an Authorization header.
   */
  async function download(path, fallbackName) {
    setBusy(path);
    setResult(null);
    try {
      const token = await getAccessToken();
      const res = await fetch(`/api${path}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);

      const disposition = res.headers.get('content-disposition') ?? '';
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;

      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      setResult({ ok: true, message: `Saved ${name}` });
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel
      title="Export & Save"
      lead="The database is the source of truth — these exports are for backup and for getting the numbers into Excel."
      note="Every figure in these files is computed by the same engine that draws the tiles, so an exported number can never disagree with the same number on screen. Importing a configuration always creates a NEW scenario rather than overwriting the live one: reviewing an imported file before promoting it is cheap, and discovering afterwards that an import replaced the organization's real data is not."
    >
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
        <div className="kpi" style={{ gridColumn: '1 / -1' }}>
          <div className="kpi-label">Excel workbook</div>
          <div className="kpi-sub" style={{ marginBottom: 10 }}>
            Five sheets: department status, Built positions, roles, the full team (with a
            roster status on every record), and the assumptions behind every figure.
          </div>
          <button
            className="btn btn-primary"
            disabled={busy !== null}
            onClick={() => download('/scenarios/live/export.xlsx', 'CapaciTEA.xlsx')}
          >
            {busy?.endsWith('.xlsx') ? 'Preparing…' : 'Download .xlsx'}
          </button>
        </div>

        <div className="kpi" style={{ gridColumn: '1 / -1' }}>
          <div className="kpi-label">Configuration backup</div>
          <div className="kpi-sub" style={{ marginBottom: 10 }}>
            The whole configuration as JSON — {state.depts.length} departments,{' '}
            {state.roles.length} roles, {state.team.length} roster records and{' '}
            {state.headcountPlan.length} position rows.
          </div>
          <button
            className="btn btn-secondary"
            disabled={busy !== null}
            onClick={() => download('/scenarios/live/export', 'CapaciTEA.json')}
          >
            {busy === '/scenarios/live/export' ? 'Preparing…' : 'Download .json'}
          </button>
        </div>
      </div>

      {result && (
        <div className="alert" style={{ marginTop: 14, ...(result.ok
            ? { background: 'var(--ltgrn)', color: 'var(--grn)', border: '1px solid var(--grn)' }
            : { background: 'var(--ltred)', color: 'var(--red)', border: '1px solid var(--red)' }) }}>
          {result.message}
        </div>
      )}

      {!isAdmin && (
        <p className="page-note">Importing a configuration requires the Admin role.</p>
      )}
    </Panel>
  );
}

const VIEWS = {
  depts: Departments,
  roles: Roles,
  activities: Activities,
  haircut: Haircuts,
  settings: Settings,
  hcplan: BuiltPositions,
  import: BuiltImport,
  quarantine: Quarantine,
  export: ExportPanel,
  selfcheck: SelfCheck,
  howto: HowItWorks,
};

export function Configure() {
  const { panel } = useParams();
  const { state } = useModel();

  // No panel selected: the catalogue. Set up once, and these settings drive every
  // calculation in the model — so it is worth showing what each area actually governs
  // before someone opens it.
  if (!panel) {
    return (
      <div className="page">
        <div className="page-hdr">
          <div className="page-hdr-title">Configuration</div>
          <div className="page-hdr-sub">Set up once — these settings drive every calculation in the model</div>
        </div>
        {CATALOGUE.map(([group, cards]) => (
          <div key={group} style={{ marginBottom: 26 }}>
            <div className="config-section-label">{group}</div>
            <div className="config-grid">
              {cards.map(([key, icon, title, count, sub]) => (
                <Link key={key} to={`/configure/${key}`} className="config-card" style={{ textDecoration: 'none' }}>
                  <div className="config-card-icon"><Icon name={icon} /></div>
                  <div className="config-card-title">
                    {title}
                    {count?.(state) && <span className="chip chip-teal" style={{ marginLeft: 8 }}>{count(state)}</span>}
                  </div>
                  <div className="config-card-sub">{sub}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const View = VIEWS[panel];
  if (!View) return <Navigate to="/configure" replace />;

  return (
    <div className="page">
      <Link to="/configure" className="back-btn">← All configuration</Link>
      <View />
    </div>
  );
}
